import { useEffect, useRef, useState } from "react";
import { PlayIcon } from "src/components/play-icon";
import { Image } from "src/components/image";
import { PlenaryMeeting, Video } from "src/types";
import { useSearchParams } from "react-router-dom";
import { KalturaPlayer, usePlayer, usePlayerConfig } from "src/lib/kaltura-player";
import { DownloadButton } from "../download-button";
import { VideoDownload } from "../video-download";
import { ShareButton } from "../share-button";
import { PlenaryShare } from "../plenary-share";
import { useDownload } from "src/lib/utils/use-download";
import { useShare } from "src/lib/utils/use-share";
import { thumbnailUrl } from "src/lib/thumbnail-url";
import useEmblaCarousel from 'embla-carousel-react';
import { NextButton, PrevButton, usePrevNextButtons } from './carousel-arrow-buttons';
import { ReactComponent as Caret } from "../../assets/caret-right.svg";

interface VideoCarouselProps {
  videos: (Video | PlenaryMeeting)[];
  scrollIntoView?: boolean;
}

export function VideoCarousel({ videos, scrollIntoView = false }: VideoCarouselProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [displayedVideo, setDisplayedVideo] = useState<Video | PlenaryMeeting | null>(null);
  const {
    setDownloads,
    otherVideoDownload,
    showDownloadButton,
    showDownloadPopup,
    toggleDownloadPopup,
    setShowDownloadPopup,
    downloadBtnRef,
    downloadPosition,
  } = useDownload(displayedVideo, false);
  const { showSharePopup, setShowSharePopup, toggleSharePopup, desktopShareBtnRef, sharePosition } = useShare(false);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const { entryId, customizeConfig } = usePlayerConfig(displayedVideo?.field_kmc_id, displayedVideo?.field_sign_kmc_id);
  const [playerId, setPlayerId] = useState("");
  const { playerPause } = usePlayer(playerId);

  useEffect(() => {
    const videoId = searchParams.get("id");
    const slideIndex = videoId ? videos.findIndex((video) => video.id === videoId) : 0;
    setActiveSlide(slideIndex);
    setDisplayedVideo(videos[slideIndex] ?? null);
    if (scrollIntoView && videoId) {
      videoRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [videos, searchParams, scrollIntoView]);

  const handlePlayerLoaded = (data: { playerId: string }) => {
    const { playerId } = data;
    if (!playerId) {
      return;
    }
    setPlayerId(playerId);
  }

  const handleDownloadClick = () => {
    toggleDownloadPopup();
    playerPause()
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: activeSlide
  })
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setActiveSlide(emblaApi.selectedScrollSnap());
      setSearchParams({ id: videos[activeSlide].id });
    }

    emblaApi.on('select', onSelect)
    onSelect();

    setDownloads({ video: videos[activeSlide] })

    return () => {
      emblaApi.off('select', onSelect);
    }
  }, [emblaApi, activeSlide, videos])

  return (
    <div className="relative" ref={videoRef}>
      <div className="relative overflow-hidden">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex touch-pan-y touch-pinch-zoom m-0">
            {videos.map((video) => (
              <div
                key={video.id}
                className="relative w-full cursor-pointer grow-0 shrink-0 basis-[1200px]"
                style={{ paddingBottom: "56.25%" }}
              >
                <Image
                  src={thumbnailUrl(video.field_thumbnail_link?.uri, "/height/393")}
                  alt={video.field_thumbnail_link?.title}
                  className="absolute left-0 top-0 w-full h-auto max-w-full max-h-full"
                />
                <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20">
                  <PlayIcon />
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end items-center mt-5 pt-5 border-t-4 border-gray-400">
           <PositionDisplay
            activeIndex={activeSlide}
            total={videos.length}
          />
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            className="mr-2 w-10 h-10 relative flex items-center justify-center p-1 bg-gray-500 disabled:bg-gray-400"
          >
            <Caret className="stroke-white rotate-180 translate-y-0 mr-0.5" />
          </PrevButton>
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            className="w-10 h-10 relative flex items-center justify-center p-1 bg-gray-500 disabled:bg-gray-400"
          >
            <Caret className="stroke-white ml-0.5" />
          </NextButton>
        </div>
      </div>

      {entryId && (
        <>
          <div className="absolute left-0 top-0 w-full" style={{ paddingBottom: "56.25%" }}>
            <KalturaPlayer
              className="absolute top-0 left-0 w-full h-full"
              entryId={entryId}
              autoplay={false}
              customizeConfig={customizeConfig}
              onPlayerLoaded={handlePlayerLoaded}
            />
          </div>
          {showDownloadButton && (
            <>
              <DownloadButton
                ref={downloadBtnRef}
                overlayed
                className="flex absolute top-2 right-12 z-50"
                handleClick={handleDownloadClick}
              />
              {showDownloadPopup && (
                <VideoDownload
                    className={downloadPosition}
                    handleClickOutside={() => setShowDownloadPopup(false)}
                    handleCancel={() => setShowDownloadPopup(false)}
                    anchors={downloadBtnRef?.current}
                    otherVideoDownload={otherVideoDownload}
                />
              )}
            </>
          )}
          <ShareButton
            ref={desktopShareBtnRef}
            overlayed
            className="flex absolute top-2 right-2 z-50"
            handleClick={toggleSharePopup}
          />
          {showSharePopup && (
            <PlenaryShare
              className={sharePosition}
              anchors={desktopShareBtnRef}
              entry={displayedVideo}
              handleClickOutside={() => setShowSharePopup(false)}
              handleCancel={() => setShowSharePopup(false)}
            ></PlenaryShare>
          )}
        </>
      )}
      <h2 className="text-2xl font-serif text-gray-900 my-3 overflow-hidden text-ellipsis">{displayedVideo?.title}</h2>
      <div
        className="mb-16 overflow-hidden"
        dangerouslySetInnerHTML={{
          __html: displayedVideo?.body?.processed ?? "",
        }}
      ></div>
    </div>
  );
}

interface PositionDisplayProps {
  activeIndex?: number;
  total?: number;
}

function PositionDisplay({ activeIndex = 0, total = 0}: PositionDisplayProps) {
 return <span className="mr-4">{ total ? activeIndex + 1 : 0 }/{total} Videos</span>
}
